import { Building, Landmark, Users, Briefcase } from 'lucide-react';

const categoryIcons = {
  'Banques & Finance': Landmark,
  'Institutions': Building,
  'Professionnels': Users,
  'Entreprises & Réseaux': Briefcase
};

const EcosystemSection = ({ content, members, images }) => {
  const founderMembers = members?.filter(m => m.category === 'founder') || [];
  const activeMembers = members?.filter(m => m.category === 'active') || [];
  const extra = content?.extra_fields || {};
  
  const categories = content?.items || [
    { category: 'Banques & Finance', examples: ["Caisse d'Epargne", 'Crédit Agricole', 'Banque Populaire'] },
    { category: 'Institutions', examples: ['Région BFC', 'CCI', 'BPI France'] },
    { category: 'Professionnels', examples: ['Experts-Comptables', 'Commissaires aux Comptes', 'Avocats'] },
    { category: 'Entreprises & Réseaux', examples: ['MEDEF', 'CPME', 'Euronext'] }
  ];

  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-inter font-medium tracking-wide uppercase rounded-full mb-4">
            {extra.badge_text || 'Notre réseau'}
          </span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            {content?.title || 'Un écosystème régional'}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#1e3a8a] to-[#D4AF37] mx-auto mb-6" />
          <p className="text-lg text-slate-600 font-inter max-w-3xl mx-auto">
            {content?.content || 'La Place Financière BFC regroupe les décideurs économiques régionaux au service du développement territorial.'}
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {categories.map((cat, idx) => {
            const IconComponent = categoryIcons[cat.category] || Building;
            return (
              <div
                key={idx}
                className="bg-slate-50 p-6 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-[#D4AF37]/20"
                data-testid={`ecosystem-category-${idx}`}
              >
                <div className="w-12 h-12 bg-[#1e3a8a]/5 rounded-lg flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-[#1e3a8a]" />
                </div>
                <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-3">
                  {cat.category}
                </h3>
                <ul className="space-y-1">
                  {(cat.examples || []).slice(0, 4).map((example, i) => (
                    <li key={i} className="text-sm text-slate-600 font-inter flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Members Logos Grid */}
        {founderMembers.length > 0 && (
          <div className="mb-12">
            <h3 className="font-playfair text-2xl font-semibold text-[#0F172A] text-center mb-8">
              {extra.founders_title || 'Membres fondateurs et de soutien'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {founderMembers.map((member, idx) => {
                const content = member.logo_url ? (
                  <img
                    src={member.logo_url}
                    alt={member.name}
                    className="max-h-12 max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-inter text-slate-600 text-center leading-tight">${member.name}</span>`;
                    }}
                  />
                ) : (
                  <span className="text-sm font-inter text-slate-600 text-center leading-tight">
                    {member.name}
                  </span>
                );

                return member.website ? (
                  <a
                    key={member.id || idx}
                    href={member.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-50 p-6 flex items-center justify-center h-24 hover:bg-white hover:shadow-md transition-all duration-300 member-logo cursor-pointer"
                    data-testid={`member-logo-${idx}`}
                    title={member.name}
                  >
                    {content}
                  </a>
                ) : (
                  <div
                    key={member.id || idx}
                    className="bg-slate-50 p-6 flex items-center justify-center h-24 hover:bg-white hover:shadow-md transition-all duration-300 member-logo"
                    data-testid={`member-logo-${idx}`}
                    title={member.name}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Members */}
        {activeMembers.length > 0 && (
          <div>
            <h3 className="font-playfair text-2xl font-semibold text-[#0F172A] text-center mb-8">
              {extra.active_title || 'Membres actifs'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {activeMembers.map((member, idx) => {
                const content = member.logo_url ? (
                  <img
                    src={member.logo_url}
                    alt={member.name}
                    className="max-h-10 max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-xs font-inter text-slate-600 text-center leading-tight">${member.name}</span>`;
                    }}
                  />
                ) : (
                  <span className="text-xs font-inter text-slate-600 text-center leading-tight">
                    {member.name}
                  </span>
                );

                return member.website ? (
                  <a
                    key={member.id || idx}
                    href={member.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-50 p-4 flex items-center justify-center h-20 hover:bg-white hover:shadow-md transition-all duration-300 cursor-pointer"
                    data-testid={`active-member-${idx}`}
                    title={member.name}
                  >
                    {content}
                  </a>
                ) : (
                  <div
                    key={member.id || idx}
                    className="bg-slate-50 p-4 flex items-center justify-center h-20 hover:bg-white hover:shadow-md transition-all duration-300"
                    data-testid={`active-member-${idx}`}
                    title={member.name}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Join CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-slate-50 to-slate-100 p-8 lg:p-12">
          <h3 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
            {extra.cta_title || 'Devenir membre'}
          </h3>
          <p className="text-slate-600 font-inter max-w-2xl mx-auto mb-6">
            {extra.cta_description || "La Place Financière, association à but non lucratif, assure l'indépendance de son action grâce au soutien financier de ses membres."}
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="inline-flex items-center gap-2 bg-[#1e3a8a] hover:bg-[#172554] text-white px-8 py-4 font-inter font-medium rounded-full transition-all duration-300 hover:shadow-lg"
            data-testid="ecosystem-join-btn"
          >
            {extra.cta_button || 'Nous rejoindre'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default EcosystemSection;
